/**
 * useRealtimeInsights Hook
 * Subscribes to realtime changes on the insights table
 * Invalidates TanStack Query cache when changes occur
 * Only active when using Supabase backend (VITE_USE_BACKEND=true)
 */

import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getSupabaseClient, type InsightRow } from '@/lib/supabase'
import { config } from '@/lib/config'
import { INSIGHTS_QUERY_KEY } from './useInsights'
import type { RealtimeChannel } from '@supabase/supabase-js'

interface UseRealtimeInsightsProps {
  enabled?: boolean
}

export function useRealtimeInsights({
  enabled = true,
}: UseRealtimeInsightsProps = {}) {
  const queryClient = useQueryClient()
  const channelRef = useRef<RealtimeChannel | null>(null)

  useEffect(() => {
    // Only subscribe if using backend and enabled
    if (!config.useBackend || !enabled) {
      return
    }

    const supabase = getSupabaseClient()
    if (!supabase) {
      return
    }

    // Create realtime channel
    const channel = supabase
      .channel('insights-realtime')
      .on<InsightRow>(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'insights',
        },
        () => {
          // Invalidate cache to refetch with new insight
          console.log('🔴 Realtime: New insight detected, invalidating cache')
          queryClient.invalidateQueries({ queryKey: INSIGHTS_QUERY_KEY })
        }
      )
      .on<InsightRow>(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'insights',
        },
        () => {
          // Invalidate cache to refetch with updated insight
          console.log('🔴 Realtime: Insight updated, invalidating cache')
          queryClient.invalidateQueries({ queryKey: INSIGHTS_QUERY_KEY })
        }
      )
      .on<InsightRow>(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'insights',
        },
        () => {
          // Invalidate cache to refetch without deleted insight
          console.log('🔴 Realtime: Insight deleted, invalidating cache')
          queryClient.invalidateQueries({ queryKey: INSIGHTS_QUERY_KEY })
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('🔴 Realtime: Connected to insights channel')
        } else if (status === 'CHANNEL_ERROR') {
          console.error('🔴 Realtime: Channel error')
        }
      })

    channelRef.current = channel

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        console.log('🔴 Realtime: Disconnecting from insights channel')
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [enabled, queryClient])

  return {
    isConnected: channelRef.current !== null,
  }
}
